export class AudioOutput extends EventTarget {
  constructor(player) {
    super();
    this.player = player;
    this.generation = 0;
    this.active = false;
    this.muted = false;
    this.receive = ({ detail }) => {
      if (!this.active || detail.generation !== this.generation) return;
      this.node?.port.postMessage({ type: 'samples', ...detail }, [detail.samples.buffer]);
    };
    player.addEventListener('audio', this.receive);
  }
  async activate() {
    if (!this.context) {
      const Context = window.AudioContext ?? window.webkitAudioContext;
      if (!Context) throw new Error('Web Audio is unavailable in this browser');
      this.context = new Context();
      this.context.onstatechange = () => {
        if (this.context.state !== 'running' && this.active) {
          this.active = false;
          this.player.pause().catch(() => {});
          this.flush(false);
          this.dispatchEvent(new Event('suspended'));
        }
      };
      try {
        await this.context.audioWorklet.addModule(new URL('./audio-worklet.js', import.meta.url));
        this.node = new AudioWorkletNode(this.context, 'bizmuth-audio', { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
        this.gain = this.context.createGain();
        this.gain.gain.value = this.muted ? 0 : 1;
        this.node.connect(this.gain).connect(this.context.destination);
        this.ready = new Promise(resolve => { this.resolveReady = resolve; });
        this.node.port.onmessage = ({ data }) => {
          if (data.ready) { this.resolveReady(); return; }
          if (data.clockMs !== undefined) {
            if (this.active && data.generation === this.generation) this.player.request('clock', data).catch(() => {});
            return;
          }
          this.stats = data;
          this.dispatchEvent(new CustomEvent('stats', { detail: data }));
        };
      } catch (error) {
        await this.context.close();
        this.context = null;
        throw error;
      }
    }
    await this.context.resume();
    let timeout;
    try {
      await Promise.race([this.ready, new Promise((resolve, reject) => {
        timeout = setTimeout(() => reject(new Error('Audio output did not start. Check the browser audio device and try again.')), 5000);
      })]);
    } finally { clearTimeout(timeout); }
    return this.context.sampleRate;
  }
  flush(active) {
    this.generation++;
    this.node?.port.postMessage({ type: 'clear', active, generation: this.generation });
  }
  async resume() {
    await this.activate();
    this.active = true;
    this.flush(true);
    await this.player.resume(this.generation, true);
  }
  async pause() {
    this.active = false;
    this.flush(false);
    await this.player.pause();
    await this.context?.suspend();
  }
  async reset() {
    const running = this.active;
    if (running) await this.player.pause();
    this.flush(running);
    await this.player.reset(this.generation);
    if (running) await this.player.resume(this.generation, true);
  }
  mute(muted) { this.muted = muted; if (this.gain) this.gain.gain.value = muted ? 0 : 1; }
  async dispose() {
    this.active = false;
    this.player.removeEventListener('audio', this.receive);
    this.node?.disconnect();
    this.node?.port.close();
    this.gain?.disconnect();
    if (this.context) { this.context.onstatechange = null; await this.context.close(); }
    this.context = null;
    this.node = null;
  }
}
